import { useEffect, useState } from 'react';
import { Redirect } from 'wouter';
import type { AccessCapability, AccessRole } from '@services/access';
import { ACCESS_STALE_EVENT, homePathForAccessRole, roleCan, useMeAccess } from '@services/access';
import { useTranslation } from '@infra/i18n';
import { Button } from '@shared/ui';
import { SkeletonTable } from '@shared/ui/layout';

/**
 * Gate a route on the access role from useMeAccess, by capability or by role list.
 * JWT checks stay in RequireAuthWouter.
 */
export function RequireAccess({
  roles,
  capability,
  children,
}: (
  | { roles: readonly AccessRole[]; capability?: never }
  | { capability: AccessCapability; roles?: never }
) & {
  children: React.ReactNode;
}) {
  const { role, isLoading, isError, retry } = useMeAccess();
  const { t } = useTranslation();
  const granted = capability ? roleCan(role, capability) : (roles ?? []).includes(role);
  const allowed = !isLoading && !isError && granted;
  // Once admitted, a failed refetch must not replace the page with a retry screen.
  const [admitted, setAdmitted] = useState(false);
  useEffect(() => {
    if (allowed) setAdmitted(true);
  }, [allowed]);
  // The cached role may be stale. Refetch it; AccessRoleSync then moves a changed role.
  useEffect(() => {
    if (!isLoading && !isError && !allowed) window.dispatchEvent(new Event(ACCESS_STALE_EVENT));
  }, [isLoading, isError, allowed]);

  if (isLoading) {
    return (
      <div className="p-6" data-testid="access-loading">
        <SkeletonTable />
      </div>
    );
  }

  // A failed access call says nothing about the role, so offer a retry instead of redirecting.
  if (isError && !admitted) {
    return (
      <div className="p-6" data-testid="access-error">
        <Button type="button" onClick={retry}>
          {t('common:buttons.retry')}
        </Button>
      </div>
    );
  }

  if (!allowed) {
    return <Redirect href={homePathForAccessRole(role)} />;
  }

  return <>{children}</>;
}
